/**
 * EmailStatus — reusable inline badge + resend button for any admin entity.
 *
 * Props:
 *   entityId   (string)   — booking_id / appointment_id / lead_id
 *   emailType  (string)   — e.g. "trial_booking_customer_confirmed"
 *   label      (string)   — short human label shown next to status icon
 *   compact    (bool)     — single-line variant (icon + label + tiny age)
 *
 * Renders:
 *   ✅ Sent · 2 hours ago    [Resend]
 *   ❌ Failed · "SMTP timeout" [Retry]
 *   ⏳ Pending                [Retry now]
 */
import { useEffect, useState, useCallback } from "react";
import { CheckCircle2, XCircle, Clock, Loader2, RotateCw, Eye, MousePointerClick, Mail, AlertTriangle } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN = () => localStorage.getItem("adminToken") || "";
const auth = () => ({ Authorization: `Bearer ${TOKEN()}` });

function relativeAge(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  if (isNaN(ms)) return "";
  const m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h} hour${h !== 1 ? "s" : ""} ago`;
  const d = Math.floor(h / 24);
  return `${d} day${d !== 1 ? "s" : ""} ago`;
}

export function EmailStatusBadge({ entityId, emailType, label, compact = false, onRefresh }) {
  const [status, setStatus] = useState({ loading: true });
  const [resending, setResending] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!entityId || !emailType) {
      setStatus({ loading: false, state: "pending" });
      return;
    }
    try {
      const res = await axios.get(`${API}/email/status/${entityId}`, { headers: auth() });
      const match = (res.data?.emails || []).find(e => e.email_type === emailType);
      if (!match) {
        setStatus({ loading: false, state: "pending" });
      } else {
        // Derive the highest-priority state from the webhook-driven timestamps.
        // Falls back to match.status for non-webhook rows.
        let state = match.status || "sent";
        if (match.complained_at) state = "complained";
        else if (match.bounced_at) state = "bounced";
        else if (match.clicked_at) state = "clicked";
        else if (match.opened_at) state = "opened";
        else if (match.delivered_at) state = "delivered";
        else if (match.status === "failed_permanent") state = "failed_permanent";
        else if (match.status === "failed") state = "failed";
        else if (match.status === "pending") state = "pending";
        setStatus({
          loading: false,
          state,
          sent_at: match.sent_at,
          delivered_at: match.delivered_at,
          opened_at: match.opened_at,
          clicked_at: match.clicked_at,
          bounced_at: match.bounced_at,
          complained_at: match.complained_at,
          open_count: match.open_count || 0,
          click_count: match.click_count || 0,
          bounce_reason: match.bounce_reason,
          to_email: match.to_email,
          attempts: match.attempts,
          error_message: match.error_message,
        });
      }
    } catch {
      setStatus({ loading: false, state: "pending" });
    }
  }, [entityId, emailType]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const handleResend = async () => {
    setResending(true);
    try {
      const res = await axios.post(`${API}/email/resend`,
        { booking_id: entityId, email_type: emailType },
        { headers: auth() });
      if (res.data?.ok) toast.success(`${label || "Email"} resent`);
      else toast.error(`Failed to resend — check error details`);
      await fetchStatus();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
    } finally {
      setResending(false);
    }
  };

  if (status.loading) {
    return <span className="inline-flex items-center gap-1.5 text-[11px] text-[#676664]/40"><Loader2 className="w-3 h-3 animate-spin" />{label}</span>;
  }

  const cfg = {
    sent:             { icon: Mail,              color: "#6b7280", text: "Sent" },
    delivered:        { icon: CheckCircle2,      color: "#10b981", text: "Delivered" },
    opened:           { icon: Eye,               color: "#3b82f6", text: "Opened" },
    clicked:          { icon: MousePointerClick, color: "#8b5cf6", text: "Clicked" },
    bounced:          { icon: XCircle,           color: "#dc2626", text: "Bounced" },
    complained:       { icon: AlertTriangle,     color: "#b91c1c", text: "Complained" },
    pending:          { icon: Clock,             color: "#f59e0b", text: "Pending" },
    failed:           { icon: XCircle,           color: "#ef4444", text: "Failed" },
    failed_permanent: { icon: XCircle,           color: "#ef4444", text: "Failed (3x)" },
  }[status.state] || { icon: Clock, color: "#9ca3af", text: status.state };

  const Icon = cfg.icon;
  // Show the most informative timestamp for the current state
  const dt =
    status.state === "clicked"    ? status.clicked_at :
    status.state === "opened"     ? status.opened_at :
    status.state === "delivered"  ? status.delivered_at :
    status.state === "bounced"    ? status.bounced_at :
    status.state === "complained" ? status.complained_at :
    status.sent_at;
  const ageStr = dt ? relativeAge(dt) : "";
  const countSuffix =
    status.state === "opened"  && status.open_count > 1  ? ` (${status.open_count}x)` :
    status.state === "clicked" && status.click_count > 1 ? ` (${status.click_count}x)` : "";

  if (compact) {
    return (
      <div className="inline-flex items-center gap-2" data-testid={`email-status-${emailType}-${entityId}`}>
        <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: cfg.color }} />
        <span className="text-[11px] text-[#5A5A5A]">{label}</span>
        <span className="text-[10px] text-[#676664]/50">{cfg.text}{countSuffix}{ageStr ? ` · ${ageStr}` : ""}</span>
        <button onClick={handleResend} disabled={resending}
          className="text-[10px] text-[#9A7B4F] hover:text-[#8B6B3F] tracking-wider uppercase inline-flex items-center gap-1 disabled:opacity-40 ml-1"
          data-testid={`resend-${emailType}-${entityId}`}>
          {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}Resend
        </button>
      </div>
    );
  }

  return (
    <div className="flex items-start justify-between gap-3 py-2 border-b border-[#282828]/5 last:border-0" data-testid={`email-status-${emailType}-${entityId}`}>
      <div className="flex items-start gap-2 min-w-0">
        <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: cfg.color }} />
        <div className="min-w-0">
          <div className="text-[12px] text-[#282828] font-medium truncate">{label}</div>
          <div className="text-[10px] text-[#676664]/60">
            {cfg.text}{countSuffix}{ageStr ? ` · ${ageStr}` : ""}{status.to_email ? ` · ${status.to_email}` : ""}
          </div>
          {status.bounce_reason && (
            <div className="text-[10px] text-red-600/80 mt-0.5 truncate" title={status.bounce_reason}>
              {status.bounce_reason}
            </div>
          )}
          {status.error_message && (
            <div className="text-[10px] text-red-600/80 mt-0.5 truncate" title={status.error_message}>
              {status.error_message}
            </div>
          )}
        </div>
      </div>
      <button onClick={handleResend} disabled={resending}
        className="text-[10px] text-[#9A7B4F] hover:bg-[#9A7B4F]/5 border border-[#9A7B4F]/30 px-2.5 py-1 tracking-wider uppercase inline-flex items-center gap-1 disabled:opacity-40 flex-shrink-0"
        data-testid={`resend-${emailType}-${entityId}`}>
        {resending ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCw className="w-3 h-3" />}Resend
      </button>
    </div>
  );
}


/**
 * EmailStatusList — show ALL emails for an entity (groups by email_type).
 * Renders nothing if no logs exist yet.
 */
export function EmailStatusList({ entityId, types = [], onRefresh }) {
  // types: [{ key: "trial_booking_customer_confirmed", label: "Customer confirmation" }, ...]
  if (!entityId || types.length === 0) return null;
  return (
    <div className="bg-[#FAF8F0]/60 border border-[#282828]/8 px-3 py-2" data-testid={`email-status-list-${entityId}`}>
      <div className="text-[10px] uppercase tracking-wider text-[#676664]/50 mb-1.5">Emails</div>
      <div>
        {types.map(t => (
          <EmailStatusBadge key={t.key} entityId={entityId} emailType={t.key} label={t.label} onRefresh={onRefresh} />
        ))}
      </div>
    </div>
  );
}
